import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Modal,
  message,
  Checkbox,
  InputNumber,
  Alert,
  Table,
  Space,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { AppState } from '../../store';
import { functions } from '../../firebase';
import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Intl } from '../../general/enums';
import Icon from '@ant-design/icons';
import DaFlag from '../../assets/flags/da';
import GbFlag from '../../assets/flags/gb';
import SeFlag from '../../assets/flags/se';
import DeFlag from '../../assets/flags/de';
import { getPassword } from '../../general/passwordGenerator';
import NlFlag from '../../assets/flags/nl';
import { isAdmin } from '../../components/AdminOnly';
import { useSelector } from 'react-redux';
import { IOrg } from '../../modules/orgs/interfaces';
import OrgSelectionShortcuts from './OrgSelectionShortcuts';

const FormItem = Form.Item;
const Option = Select.Option;

const formItemLayout = {
  wrapperCol: { span: 24 },
};

export const prefixSelector = (
  <Form.Item name="phoneCode" noStyle>
    <Select style={{ width: 100 }}>
      <Option value="45">
        <DaFlag style={{ marginBottom: -2 }} /> +45
      </Option>
      <Option value="31">
        <NlFlag style={{ marginBottom: -2 }} /> +31
      </Option>
    </Select>
  </Form.Item>
);

function AddMemberForm({ orgId, allOrgs }: any) {
  const [form] = Form.useForm();
  const [visible, setvisible] = useState(false);
  const [confirmLoading, setconfirmLoading] = useState(false);
  const { t } = useTranslation();
  const [isNewUser, setIsNewUser] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDanish, setIsDanish] = useState<boolean>(true);
  const [isInvite, setIsInvite] = useState<boolean>(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([orgId]);
  const [orgSearchText, setOrgSearchText] = useState<string>('');
  const [showOrgSelector, setShowOrgSelector] = useState<boolean>(false);
  const uid = useSelector((state: AppState) => state.user.details.uid);
  const currentUserIsAdmin = isAdmin(uid);

  useEffect(() => {
    form.validateFields();
    return () => {};
  }, []);

  // Filter orgs that the admin has access to
  // const adminAccessibleOrgs = useMemo(() => {
  //   if (!currentUserIsAdmin || !allOrgs) return [];
  //   return allOrgs.filter((org: IOrg) => {
  //     const member = org.members?.[uid];
  //     return member !== undefined; // Admin has access to orgs where they are a member
  //   });
  // }, [currentUserIsAdmin, allOrgs, uid]);

  // Filter orgs based on search text
  const filteredOrgs = useMemo(() => {
    if (!orgSearchText) return allOrgs;
    const searchLower = orgSearchText.toLowerCase();
    return allOrgs.filter(
      (org: IOrg) =>
        org.title?.toLowerCase().includes(searchLower) || org.id.toLowerCase().includes(searchLower)
    );
  }, [orgSearchText, allOrgs]);
  const showModal = () => setvisible(true);

  const handleOk = async (values: any) => {
    if (currentUserIsAdmin && selectedOrgIds.length === 0) {
      setShowOrgSelector(true);
      setError(t('access.add.org_required'));
      return;
    }
    setconfirmLoading(true);

    const orgsToAddTo = currentUserIsAdmin ? selectedOrgIds : [orgId];

    try {
      const functionRef = functions.httpsCallable('settings');
      const response = await functionRef({
        requestType: 'PRE_USER_CREATION',
        orgIds: orgsToAddTo, // Send array of org IDs for batch processing
        email: values.email,
        password: values.password,
        role: values.role,
        name: values.name,
        invite: values.invite,
        guides: values.guides,
        intl: values.intl,
        phoneCode: values.phoneCode,
        phone: values.phone,
      });

      setconfirmLoading(false);
      setError(null);
      form.resetFields();
      setvisible(false);
      setIsNewUser(null);
      setSelectedOrgIds([orgId]); // Reset to current org

      if (orgsToAddTo.length > 1) {
        message.success(
          `${t('access.add.success')} - Added to ${orgsToAddTo.length} organization(s)`
        );
      } else {
        message.success(t('access.add.success'));
      }
    } catch (error: any) {
      setconfirmLoading(false);

      // Check if user doesn't exist (needs new user fields)
      // Firebase callable functions return errors with code property
      if (
        error.code === 'not-found' ||
        error.code === 'auth/user-not-found' ||
        error.message?.includes('User not found')
      ) {
        setIsNewUser(true);
        setError(null);
      } else {
        setError(t('access.add.error'));
      }
    }
  };

  const handleCancel = () => {
    setvisible(false);
    setSelectedOrgIds([orgId]); // Reset to current org
    setIsNewUser(null);
    setError(null);
    setOrgSearchText('');
    setShowOrgSelector(false);
  };

  const handleOrgSelection = (selectedRowKeys: React.Key[]) => {
    setSelectedOrgIds(selectedRowKeys as string[]);
    setError(null);
  };

  const orgTableColumns = [
    {
      title: 'Organization',
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IOrg, b: IOrg) => {
        if (a.title && b.title) {
          return a.title.localeCompare(b.title, 'da', {
            sensitivity: 'base',
            numeric: true,
          });
        }
        return 0;
      },
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
    },
  ];

  const onLanguageChange = (value: Intl) => {
    const truthy = value === Intl.da_DK;
    setIsDanish(truthy);
  };
  const onInviteChange = (e: any) => setIsInvite(e.target.checked);

  return (
    <>
      <Button type="primary" size="large" onClick={showModal}>
        {t('access.add.button')}
      </Button>
      <Modal
        title={t('access.add.headline')}
        open={visible}
        // onOk={e => handleOk(e, email)}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        footer={[]}
        width={currentUserIsAdmin ? 800 : 520}
      >
        <Form
          autoComplete="false"
          form={form}
          initialValues={{
            password: getPassword(),
            intl: Intl.da_DK,
            phoneCode: '45',
            guides: false,
          }}
          onFinish={handleOk}
        >
          {currentUserIsAdmin && allOrgs.length > 0 && (
            <FormItem {...formItemLayout} style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  onClick={() => setShowOrgSelector(!showOrgSelector)}
                  style={{ width: '100%' }}
                >
                  {showOrgSelector ? 'Hide' : 'Select'} Organizations ({selectedOrgIds.length}{' '}
                  selected)
                </Button>

                {showOrgSelector && (
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 12 }}>
                    <OrgSelectionShortcuts
                      orgs={allOrgs}
                      selectedOrgIds={selectedOrgIds}
                      onChange={(ids) => {
                        setSelectedOrgIds(ids);
                        setError(null);
                      }}
                    />
                    <Input
                      placeholder="Search organizations..."
                      prefix={<SearchOutlined />}
                      value={orgSearchText}
                      onChange={(e) => setOrgSearchText(e.target.value)}
                      style={{ marginBottom: 12 }}
                    />
                    <Table
                      columns={orgTableColumns}
                      dataSource={filteredOrgs}
                      rowKey="id"
                      pagination={false}
                      scroll={{ y: 300 }}
                      size="small"
                      rowSelection={{
                        selectedRowKeys: selectedOrgIds,
                        onChange: handleOrgSelection,
                        preserveSelectedRowKeys: true,
                      }}
                    />
                  </div>
                )}

                <Alert
                  message={`User will be added to ${selectedOrgIds.length} organization(s) with the same role and settings`}
                  type={selectedOrgIds.length === 0 ? 'warning' : 'info'}
                  showIcon
                />
              </Space>
            </FormItem>
          )}
          <FormItem
            {...formItemLayout}
            name="email"
            rules={[{ required: true, message: t('access.add.email_required') }]}
          >
            <Input
              autoComplete="false"
              role="presentation"
              type="email"
              prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder={t('access.add.email_placeholder')}
            />
          </FormItem>
          <FormItem
            {...formItemLayout}
            style={{ minWidth: '150px' }}
            name="role"
            rules={[{ required: true, message: t('access.add.role_required') }]}
          >
            <Select placeholder={t('access.add.role_placeholder')}>
              <Option value="cleaner">{t('access.role_cleaner')}</Option>
              <Option value="departmentExecutive">{t('access.role_executive')}</Option>
              <Option value="sales">{t('access.role_sales')}</Option>
            </Select>
          </FormItem>
          {isNewUser && (
            <FormItem
              {...formItemLayout}
              name="password"
              rules={[
                {
                  required: true,
                  message: t('access.add.password_required'),
                },
              ]}
            >
              <Input.Password
                autoComplete="new-password"
                // type="password"
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder={t('access.add.password_placeholder')}
              />
            </FormItem>
          )}
          {isNewUser && (
            <FormItem
              name="name"
              rules={[
                {
                  required: true,
                  message: t('access.add.name_required'),
                },
              ]}
            >
              <Input
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder={t('access.add.name_placeholder')}
              />
            </FormItem>
          )}
          {isNewUser && (
            <Form.Item
              name="phone"
              rules={[
                {
                  required: false,
                  message: 'Please input your phone number!',
                },
              ]}
            >
              <Input
                addonBefore={prefixSelector}
                style={{ width: '100%' }}
                type="tel"
                maxLength={12}
                pattern="[0-9]{8}|[0-9]{9}|[0-9]{10}|[0-9]{11}|[0-9]{12}"
                placeholder={t('access.add.phone_placeholder')}
              />
            </Form.Item>
          )}

          {isNewUser && (
            <FormItem name="intl" label={t('access.add.user_language')}>
              <Select onChange={onLanguageChange}>
                <Option value={Intl.da_DK}>
                  <Icon component={DaFlag} /> {t('profile.danish')}
                </Option>
                <Option value={Intl.en_US}>
                  <Icon component={GbFlag} /> {t('profile.english')}
                </Option>
                <Option value={Intl.se_SE}>
                  <Icon component={SeFlag} /> {t('profile.swedish')}
                </Option>
                <Option value={Intl.de_DE}>
                  <Icon component={DeFlag} /> {t('profile.german')}
                </Option>
              </Select>
            </FormItem>
          )}
          {isNewUser && (
            <>
              <FormItem name="invite" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox onChange={onInviteChange}>
                  {t('access.add.send_automated_invite')}
                </Checkbox>
              </FormItem>
              <FormItem name="guides" valuePropName="checked">
                <Checkbox disabled={!isDanish || !isInvite}>
                  {t('access.add.attach_guides_invite')}
                </Checkbox>
              </FormItem>
            </>
          )}
          <FormItem>
            <Button type="primary" block htmlType="submit" loading={confirmLoading}>
              {t('access.add.submit')}
            </Button>
          </FormItem>
        </Form>
        {error && <div style={{ color: 'red' }}>{error}</div>}
      </Modal>
    </>
  );
}

function mapStateToProps(state: AppState) {
  return {
    orgId: state.orgs.currentOrg.id,
    allOrgs: state.orgs.orgs,
  };
}

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(AddMemberForm);
